import http from "./http.service";
import {hostDeTienda} from "./tiendaHost";

/**
 * La tienda propia en la que corre el front (si corre en una) y la configuracion del vendedor.
 *
 * `cargar()` se llama una vez antes de montar la app (index.js): con eso cada pantalla sabe con
 * `actual()` si esta en una tienda, y la marca (titulo, colores, favicon) ya esta aplicada.
 */
class TiendaService {

    private tienda: any = null;

    /** Resuelve null en el sitio general; rechaza si el subdominio no es una tienda habilitada. */
    cargar(): Promise<any> {
        if (!hostDeTienda()) return Promise.resolve(null);
        return http.get("/public/tienda/contexto").then((r: any) => {
            this.tienda = r.tienda;
            this.aplicarMarca(this.tienda);
            return this.tienda;
        });
    }

    actual(): any {
        return this.tienda;
    }

    esTienda(): boolean {
        return this.tienda != null;
    }

    /** La tienda vende usados (desarmadero): ahi si existen las bajas. */
    esDeUsados(): boolean {
        return this.tienda?.rubro === "USADOS";
    }

    private aplicarMarca(t: any) {
        if (!t) return;
        document.title = t.nombre;
        const raiz = document.documentElement;
        raiz.setAttribute("data-tienda", t.subdominio);
        const variable = (nombre: string, valor: string) =>
            [raiz, document.body].forEach((e) => e.style.setProperty(nombre, valor));
        // Los colores de la tienda pisan los del sitio: --accent-500 es el de botones y links, y
        // --primary-500 el de los destacados. Van inline en <html> (le gana al :root de main.css) y
        // tambien en <body>: cuando hay data-theme, body[data-theme] redefine la paleta en el body y
        // la tienda perdia sus colores en todas las pantallas que fijan el tema (el home, por ejemplo).
        /*
         * El color de la marca usado como texto (links, precios, titulos) tiene que leerse sobre el
         * fondo de cada tema: se deja una version para light y otra para dark, y _tienda.scss elige
         * segun el tema activo. Si el color ya alcanza, queda tal cual.
         */
        const aplicarTextos = (nombre: string, rgb: number[]) => {
            variable(`--tienda-${nombre}-texto-light`, legibleSobre(rgb, FONDO_LIGHT, 0));
            variable(`--tienda-${nombre}-texto-dark`, legibleSobre(rgb, FONDO_DARK, 255));
        };
        if (t.colorPrimario) {
            variable("--accent-500", t.colorPrimario);
            const rgb = hexARgb(t.colorPrimario);
            if (rgb) {
                variable("--tienda-texto-accent", textoLegibleSobre(rgb));
                aplicarTextos("accent", rgb);
            }
        }
        if (t.colorSecundario) {
            variable("--primary-500", t.colorSecundario);
            // El pie y los hovers usan tambien el 400 y el 600, y el texto encima esta pensado para
            // el celeste del sitio: con un color oscuro no se leia. Se derivan del color elegido.
            const rgb = hexARgb(t.colorSecundario);
            if (rgb) {
                variable("--primary-600", mezclar(rgb, 0, 0.15));
                variable("--primary-400", mezclar(rgb, 255, 0.15));
                variable("--tienda-texto-primario", textoLegibleSobre(rgb));
                aplicarTextos("primario", rgb);
            }
        }
        if (t.logoUrl) {
            document.querySelectorAll("link[rel~='icon']").forEach((l: any) => (l.href = t.logoUrl));
        }
        const theme = document.querySelector("meta[name='theme-color']");
        if (theme && t.colorPrimario) theme.setAttribute("content", t.colorPrimario);
    }

    // --- "Configurar mi tienda" (panel del vendedor) ---

    config(): Promise<any> {
        return http.get("/api/cuenta/tienda").then((r: any) => r.config);
    }

    guardar(datos: any): Promise<any> {
        return http.post("/api/cuenta/tienda", datos).then((r: any) => r.config);
    }

    disponible(subdominio: string): Promise<any> {
        return http.get("/api/cuenta/tienda/disponible", {params: {s: subdominio}});
    }

    subirLogo(archivo: File): Promise<any> {
        const form = new FormData();
        form.append("archivo", archivo);
        return http.post("/api/cuenta/tienda/logo", form, {headers: {"Content-Type": "multipart/form-data"}})
            .then((r: any) => r.config);
    }

    quitarLogo(): Promise<any> {
        return http.delete("/api/cuenta/tienda/logo").then((r: any) => r.config);
    }

    catalogo(): Promise<any> {
        return http.get("/api/cuenta/tienda/catalogo");
    }

    guardarCatalogo(categoriasOcultas: number[], tiposOcultos: number[]): Promise<any> {
        return http.post("/api/cuenta/tienda/catalogo", {categoriasOcultas, tiposOcultos});
    }

    clientes(page = 0, size = 20): Promise<any> {
        return http.get("/api/cuenta/tienda/clientes", {params: {page, size}}).then((r: any) => r.clientes);
    }

    // --- Back office ---

    adminFijarSubdominio(cuentaId: number, subdominio: string): Promise<any> {
        return http.post(`/api/admin/suscripciones/cuenta/${cuentaId}/subdominio`, {subdominio});
    }

    adminCategorias(): Promise<any> {
        return http.get("/api/admin/categorias");
    }

    adminGuardarCategoria(categoria: any): Promise<any> {
        return http.post("/api/admin/categorias", categoria).then((r: any) => r.categoria);
    }

    adminGuardarTipo(tipo: any): Promise<any> {
        return http.post("/api/admin/categorias/tipos", tipo).then((r: any) => r.tipo);
    }

    // Tipos que sugirio la IA, pendientes de revision.

    adminTiposSugeridos(): Promise<any[]> {
        return http.get("/api/admin/categorias/sugeridos").then((r: any) => r.sugeridos || []);
    }

    adminAprobarSugerido(id: number, nombre: string, categoriaId: number | null): Promise<any> {
        return http.post(`/api/admin/categorias/sugeridos/${id}/aprobar`, {nombre, categoriaId}).then((r: any) => r.tipo);
    }

    adminFusionarSugerido(id: number, destinoId: number): Promise<any> {
        return http.post(`/api/admin/categorias/sugeridos/${id}/fusionar/${destinoId}`, {});
    }

    adminDescartarSugerido(id: number): Promise<any> {
        return http.delete(`/api/admin/categorias/sugeridos/${id}`);
    }

    // Asistente "Completar con IA" del alta de publicaciones.

    iaDisponible(): Promise<boolean> {
        return http.get("/api/vendedor/ia/estado").then((r: any) => !!r.disponible).catch(() => false);
    }

    iaSugerir(texto: string): Promise<any> {
        return http.post("/api/vendedor/ia/sugerir", {texto}).then((r: any) => r.sugerencia);
    }

    iaCrearTipo(nombre: string, categoriaId: number | null, contexto: string): Promise<any> {
        return http.post("/api/vendedor/ia/tipo-sugerido", {nombre, categoriaId, contexto}).then((r: any) => r.tipo);
    }
}

/** "#1e3a8a" o "#18f" -> [r, g, b], o null si no es un hex. */
function hexARgb(hex: string): number[] | null {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || "").trim());
    if (!m) return null;
    const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
    return [0, 2, 4].map((i) => parseInt(h.substr(i, 2), 16));
}

/** El color acercado un `peso` hacia `hacia` (0 negro, 255 blanco). */
function mezclar(rgb: number[], hacia: number, peso: number): string {
    const [r, g, b] = rgb.map((c) => Math.round(c + (hacia - c) * peso));
    return `rgb(${r}, ${g}, ${b})`;
}

/*
 * Los fondos sobre los que aparece texto en el color de la marca, el peor caso de cada tema: en light
 * el gris mas oscuro (neutral-150, 90%) y en dark el mas claro (neutral-200, 17.5%, el de los hover).
 */
const FONDO_LIGHT = [230, 230, 230];
const FONDO_DARK = [45, 45, 45];

/** Luminancia relativa WCAG. */
function luminancia(rgb: number[]): number {
    const [r, g, b] = rgb.map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contraste(a: number[], b: number[]): number {
    const [l1, l2] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
    return (l1 + 0.05) / (l2 + 0.05);
}

/** Blanco o casi negro, el que mas contraste tenga sobre ese fondo. */
function textoLegibleSobre(rgb: number[]): string {
    return contraste(rgb, [255, 255, 255]) >= contraste(rgb, [20, 20, 20]) ? "#ffffff" : "#141414";
}

/**
 * El color, acercado de a poco a `hacia` (0 oscurece, 255 aclara) hasta que se lea como texto sobre
 * `fondo` (4.5:1, el minimo WCAG para texto normal). Se mueve lo justo para no perder el tono.
 */
function legibleSobre(rgb: number[], fondo: number[], hacia: number): string {
    for (let peso = 0; peso < 1; peso += 0.05) {
        const c = rgb.map((v) => Math.round(v + (hacia - v) * peso));
        if (contraste(c, fondo) >= 4.5) return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
    }
    return hacia === 0 ? "#141414" : "#ffffff";
}

const tiendaService = new TiendaService();
export default tiendaService;
